import axios from 'axios';
import Config from '../config';

// Reporting client only. The shared axios instance already attaches the ERP session
// token. These calls stay on /api/meta-ads and do not use the Meta lead webhook routes.
// qtUrl is localhost:5003 in local dev and quotationerp only for a production build.
const base = `${Config.qtUrl}meta-ads`;

function optionalNumber(value) {
    if (value == null || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function moneyNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
}

function costPerClick(row, spend, clicks) {
    const provided = optionalNumber(row.costPerClick ?? row.cpc);
    if (provided != null) return provided;
    return clicks > 0 ? spend / clicks : null;
}

function costPerLead(row, spend, leadCount) {
    if (row.leadPeriodMatches === false) return null;
    const provided = optionalNumber(row.costPerLead ?? row.cpl);
    if (provided != null) return provided;
    return leadCount != null && leadCount > 0 ? spend / leadCount : null;
}

function leadCountOf(row) {
    if (row.leadCount == null && row.leads == null) return null;
    return optionalNumber(row.leadCount ?? row.leads);
}

function campaignCities(row) {
    const fromList = Array.isArray(row.cities) ? row.cities : [];
    const names = fromList.length ? fromList : (row.city ? [row.city] : []);
    return names.map((name) => String(name || '').trim()).filter(Boolean);
}

export function campaignGroupCityTags(campaign) {
    if (campaign?.adsetCities?.length) return campaign.adsetCities;
    if (campaign?.cities?.length) return campaign.cities;
    return [];
}

export function normalizeCampaign(row = {}) {
    const spend = moneyNumber(row.spend);
    const clicks = moneyNumber(row.clicks);
    const leadCount = leadCountOf(row);
    const campaignId = String(row.campaignId || row.campaign_id || row.id || '');
    const cities = campaignCities(row);
    const adsetCities = campaignCities({ cities: row.adsetCities });

    return {
        campaignId,
        campaignName: row.campaignName || row.campaign_name || row.name || 'Untitled campaign',
        spend,
        currency: row.currency || 'INR',
        impressions: moneyNumber(row.impressions),
        clicks,
        costPerClick: costPerClick(row, spend, clicks),
        leadCount,
        sqlReal: moneyNumber(row.sqlReal),
        salesTbc: moneyNumber(row.salesTbc),
        totalReal: moneyNumber(row.totalReal != null ? row.totalReal : (moneyNumber(row.sqlReal) + moneyNumber(row.salesTbc))),
        salesWon: moneyNumber(row.salesWon),
        salesWonAmount: moneyNumber(row.salesWonAmount),
        costPerLead: costPerLead(row, spend, moneyNumber(row.totalReal != null ? row.totalReal : (moneyNumber(row.sqlReal) + moneyNumber(row.salesTbc)))),
        reach: optionalNumber(row.reach),
        cities,
        adsetCities,
        city: cities[0] || null,
    };
}

function mergeDuplicateCampaign(a, b) {
    if (Math.abs(a.spend - b.spend) < 0.01) return a;
    return a.spend >= b.spend ? a : b;
}

export function normalizeCampaigns(payload) {
    const rows = Array.isArray(payload?.campaigns)
        ? payload.campaigns
        : Array.isArray(payload) ? payload : [];

    const byId = new Map();
    rows
        .map(normalizeCampaign)
        .filter((row) => row.campaignId)
        .forEach((row) => {
            const prev = byId.get(row.campaignId);
            byId.set(row.campaignId, prev ? mergeDuplicateCampaign(prev, row) : row);
        });
    return [...byId.values()];
}

export function normalizeDaily(payload) {
    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.daily) ? payload.daily
            : Array.isArray(payload?.trend) ? payload.trend
                : Array.isArray(payload?.points) ? payload.points
                    : [];

    return rows
        .map((row) => ({
            date: String(row.date || row.day || row.date_start || '').slice(0, 10),
            spend: moneyNumber(row.spend),
            clicks: moneyNumber(row.clicks),
            impressions: moneyNumber(row.impressions),
            leadCount: leadCountOf(row),
            currency: row.currency || null,
        }))
        .filter((row) => row.date)
        .sort((a, b) => String(a.date).localeCompare(String(b.date)));
}

function levelRows(payload, level) {
    if (!payload || typeof payload !== 'object') return [];
    if (level === 'adset' && Array.isArray(payload.adsets)) return payload.adsets;
    if (level === 'ad' && Array.isArray(payload.ads)) return payload.ads;
    if (Array.isArray(payload.rows)) return payload.rows;

    const campaigns = Array.isArray(payload.campaigns) ? payload.campaigns : [];
    const matchesLevel = campaigns.some((row) => (
        level === 'adset'
            ? row.adsetId || row.adset_id
            : row.adId || row.ad_id
    ));
    if (payload.level === level || matchesLevel) return campaigns;
    return [];
}

export function normalizeLevelRows(payload, level) {
    return levelRows(payload, level).map((row) => {
        const spend = moneyNumber(row.spend);
        const clicks = moneyNumber(row.clicks);
        const leadCount = leadCountOf(row);
        const id = String(
            row.adId || row.ad_id || row.adsetId || row.adset_id || row.id || ''
        );
        const name = row.adName || row.ad_name || row.adsetName || row.adset_name || row.name || 'Untitled';

        const cities = campaignCities(row);

        return {
            id,
            name,
            spend,
            currency: row.currency || 'INR',
            impressions: moneyNumber(row.impressions),
            clicks,
            costPerClick: costPerClick(row, spend, clicks),
            leadCount,
            sqlReal: moneyNumber(row.sqlReal),
            salesTbc: moneyNumber(row.salesTbc),
            totalReal: moneyNumber(row.totalReal != null ? row.totalReal : (moneyNumber(row.sqlReal) + moneyNumber(row.salesTbc))),
            costPerLead: costPerLead(row, spend, moneyNumber(row.totalReal != null ? row.totalReal : (moneyNumber(row.sqlReal) + moneyNumber(row.salesTbc)))),
            reach: optionalNumber(row.reach),
            cities,
            city: cities[0] || null,
        };
    }).filter((row) => row.id);
}

function reportParams({ from, to, level, campaignId, adsetId, platform }) {
    const params = { from, to };
    if (level) params.level = level;
    if (campaignId) params.campaignId = campaignId;
    if (adsetId) params.adsetId = adsetId;
    if (platform && platform !== 'both') params.platform = platform;
    return params;
}

export async function getCampaignReport({ from, to, signal, level, campaignId, adsetId, platform }) {
    const { data } = await axios.get(`${base}/campaigns`, {
        params: reportParams({ from, to, level, campaignId, adsetId, platform }),
        signal,
    });
    return data;
}

export async function getAdCities({ signal } = {}) {
    const { data } = await axios.get(`${base}/cities`, { signal });
    const rows = Array.isArray(data?.cities) ? data.cities : Array.isArray(data) ? data : [];
    return rows
        .map((row) => (typeof row === 'string' ? row : row?.name))
        .map((name) => String(name || '').trim())
        .filter(Boolean);
}

export async function getLeadTargets({ signal } = {}) {
    const { data } = await axios.get(`${base}/targets`, { signal });
    const rows = Array.isArray(data?.targets) ? data.targets : [];
    return rows.reduce((map, row) => {
        const city = String(row.city || '').trim();
        const leadTarget = Number(row.leadTarget);
        if (city && Number.isInteger(leadTarget)) map[city] = leadTarget;
        return map;
    }, {});
}

export async function saveLeadTarget({ city, leadTarget }) {
    const { data } = await axios.put(`${base}/targets`, { city, leadTarget });
    return data;
}

export async function getCityDailyBreakdown({ from, to, campaignIds, platform, signal }) {
    const params = { from, to };
    if (campaignIds) params.campaignIds = campaignIds;
    if (platform && platform !== 'both') params.platform = platform;
    const { data } = await axios.get(`${base}/city-daily`, { params, signal });
    return data;
}

export async function getSpendTrend({ from, to, city, platform, signal }) {
    const params = { from, to };
    if (city) params.city = city;
    if (platform && platform !== 'both') params.platform = platform;
    const { data } = await axios.get(`${base}/daily`, { params, signal });
    return data;
}

function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

/** One poll loop / one sync POST at a time (avoids duplicate GET /sync storms). */
let syncWaitPromise = null;
let syncPostPromise = null;

async function waitForSync(startedAt) {
    if (syncWaitPromise) return syncWaitPromise;
    const began = startedAt ? new Date(startedAt).getTime() : Date.now();
    syncWaitPromise = (async () => {
        const deadline = Date.now() + 10 * 60 * 1000;
        let intervalMs = 3000;
        try {
            while (Date.now() < deadline) {
                await wait(intervalMs);
                intervalMs = Math.min(intervalMs + 750, 10000);
                const { data } = await axios.get(`${base}/sync`);
                if (data?.running) continue;
                if (data?.lastError) {
                    const error = new Error(data.lastError);
                    error.response = { data: { message: data.lastError } };
                    throw error;
                }
                const finishedAt = data?.finishedAt ? new Date(data.finishedAt).getTime() : 0;
                if (finishedAt >= began) {
                    return data?.lastResult || data;
                }
            }
            const error = new Error('Sync is still running. Refresh the report in a minute.');
            error.response = { data: { message: error.message } };
            throw error;
        } finally {
            syncWaitPromise = null;
        }
    })();
    return syncWaitPromise;
}

export async function syncCampaignReport({ from, to, incremental = true, full = false }) {
    if (syncPostPromise) return syncPostPromise;
    syncPostPromise = (async () => {
        try {
            let startedAt = Date.now();
            try {
                const { data } = await axios.post(`${base}/sync`, { from, to, incremental, full });
                if (data?.started) {
                    startedAt = data.startedAt || startedAt;
                    return waitForSync(startedAt);
                }
                return data;
            } catch (postError) {
                const status = postError?.response?.status;
                if (status === 409) {
                    return waitForSync(startedAt);
                }
                throw postError;
            }
        } finally {
            syncPostPromise = null;
        }
    })();
    return syncPostPromise;
}

export async function getMetaAdsBucketTotals({
    from,
    to,
    campaignIds,
    city,
    platform,
    signal,
}) {
    const params = { from, to };
    if (campaignIds) params.campaignIds = campaignIds;
    if (city) params.city = city;
    if (platform && platform !== 'both') params.platform = platform;
    const { data } = await axios.get(`${base}/totals`, { params, signal });
    return data;
}

export async function getMetaAdsLeadDetails({
    from,
    to,
    bucket,
    city,
    campaignId,
    campaignIds,
    adsetId,
    adId,
    platform,
    signal,
}) {
    const params = { from, to, bucket };
    if (city) params.city = city;
    if (campaignId) params.campaignId = campaignId;
    if (campaignIds) params.campaignIds = campaignIds;
    if (adsetId) params.adsetId = adsetId;
    if (adId) params.adId = adId;
    if (platform && platform !== 'both') params.platform = platform;
    const { data } = await axios.get(`${base}/leads`, { params, signal });
    return data;
}

export function adsErrorMessage(error, fallback) {
    return error?.response?.data?.message
        || error?.response?.data?.error
        || fallback;
}

export function isCanceled(error) {
    return error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError';
}
